/**
 * API Service for sending and previewing notifications.
 *
 * Backend: Alaric.Services.Messaging - NotificationsController
 * Base route: /api/v1/notifications
 */

import { Result } from "../../../models/Result";
import {
    PreviewRequest,
    RenderResult,
    SendNotificationRequest,
    SendNotificationResult,
    SendToTopicRequest,
    SendToTopicResult,
    NotificationChannel,
} from "../../../models/notifications";
import { messagingHttpService } from "./MessagingHttpService";
import { httpService } from "../../HttpService";

const BASE_PATH = "/api/v1/notifications";
const CRM_NOTIFICATION_PATH = "/Notification";

class NotificationsApiService {
    /**
     * Preview/render a notification template without sending.
     * Uses an existing template from the database.
     * @param request The preview request.
     */
    preview = async (request: PreviewRequest): Promise<Result<RenderResult>> => {
        return messagingHttpService.post<RenderResult>(`${BASE_PATH}/preview`, request);
    };

    /**
     * Preview/render a draft template without saving it first.
     * Useful for showing preview while editing in the UI.
     * @param merchantId The merchant ID.
     * @param eventCode The event code.
     * @param channel The notification channel.
     * @param draftSubject Draft subject (for Email).
     * @param draftTitle Draft title (for Push/Pull).
     * @param draftBody Draft body content.
     * @param templatePayload Variables to substitute.
     * @param languageIso Optional language ISO code.
     * @param draftDeepLink Optional draft deep link template.
     */
    previewDraft = async (
        merchantId: number,
        eventCode: string,
        channel: NotificationChannel,
        draftSubject: string | undefined,
        draftTitle: string | undefined,
        draftBody: string,
        templatePayload?: Record<string, unknown>,
        languageIso?: string,
        draftDeepLink?: string
    ): Promise<Result<RenderResult>> => {
        const request: PreviewRequest = {
            merchantId,
            eventCode,
            channel,
            draftSubject,
            draftTitle,
            draftBody,
            templatePayload,
            languageIso,
            draftDeepLink,
        };
        return this.preview(request);
    };

    /**
     * Preview an existing template by ID.
     * @param merchantId The merchant ID.
     * @param eventCode The event code.
     * @param channel The notification channel.
     * @param templatePayload Variables to substitute.
     * @param languageId Optional language GUID.
     * @param languageIso Optional language ISO code.
     */
    previewExisting = async (
        merchantId: number,
        eventCode: string,
        channel: NotificationChannel,
        templatePayload?: Record<string, unknown>,
        languageId?: string,
        languageIso?: string
    ): Promise<Result<RenderResult>> => {
        const request: PreviewRequest = {
            merchantId,
            eventCode,
            channel,
            templatePayload,
            languageId,
            languageIso,
        };
        return this.preview(request);
    };

    /**
     * Send a notification.
     * @param request The send notification request.
     */
    send = async (request: SendNotificationRequest): Promise<Result<SendNotificationResult>> => {
        return httpService.post<SendNotificationResult>(`${CRM_NOTIFICATION_PATH}/send`, request);
    };

    /**
     * Send a notification to an FCM topic.
     * One FCM API call — Firebase handles fan-out to all subscribers.
     * @param request The topic send request.
     */
    sendToTopic = async (request: SendToTopicRequest): Promise<Result<SendToTopicResult>> => {
        return messagingHttpService.post<SendToTopicResult>(`${BASE_PATH}/send-topic`, request);
    };

    /**
     * Send a notification with idempotency key to prevent duplicates.
     * @param request The send notification request.
     * @param idempotencyKey Unique key to prevent duplicate sends.
     */
    sendIdempotent = async (
        request: Omit<SendNotificationRequest, "idempotencyKey">,
        idempotencyKey: string
    ): Promise<Result<SendNotificationResult>> => {
        return this.send({ ...request, idempotencyKey });
    };
}

const notificationsApiService = new NotificationsApiService();

export { notificationsApiService };
